import { Injectable } from '@angular/core';
import { Apollo, ApolloBase } from 'apollo-angular';
import { map } from 'rxjs';
import {
  ContentDataRequestInput,
  ContentSection,
  ContentDataByHierarchyResponse,
  ContentAssignmentStudentDataResponse,
  ContentAssignmentsDataRequestInput,
  AssignmentResponseObject,
} from '../types/content-metadata';
import {
  ACTIVITY_IDS_QUERY,
  ACTIVITY_LIST_QUERY,
  ASSIGNMENTS_QUERY,
} from '../data/queries/content-metadata';
import { ContentSubject } from '../enums/content-subject';

@Injectable({
  providedIn: 'root',
})
export class ContentMetadataService {
  private apollo: ApolloBase;
  constructor(private apolloProvider: Apollo) {
    this.apollo = apolloProvider.use('contentMetadata');
  }

  public getContentByHierarchy(
    input: ContentDataRequestInput = {}
  ): Promise<ContentSection[] | undefined> {
    const v = {
      input: input,
    };

    return this.apollo
      .query<ContentDataByHierarchyResponse>({
        query: ACTIVITY_LIST_QUERY,
        variables: v,
      })
      .pipe(
        map(({ data }) => {
          return data?.microlessonByHierarchy.sections;
        })
      )
      .toPromise();
  }

  getContentAssignmentStudentData(
    input: ContentAssignmentsDataRequestInput
  ): Promise<AssignmentResponseObject[] | undefined> {
    const v = {
      input,
    };

    return this.apollo
      .query<ContentAssignmentStudentDataResponse>({
        query: ASSIGNMENTS_QUERY,
        variables: v,
        fetchPolicy: 'network-only',
      })
      .pipe(
        map(({ data }) => {
          return data?.contentAssignmentStudentData.assignments;
        })
      )
      .toPromise();
  }

  getNextUpOid(
    input: ContentDataRequestInput = {},
    nextUpUUID: string
  ): Promise<number | null | undefined> {
    const v = {
      input: input,
    };

    return this.apollo
      .query<ContentDataByHierarchyResponse>({
        query: ACTIVITY_IDS_QUERY,
        variables: v,
        fetchPolicy: 'network-only',
      })
      .pipe(
        map(({ data }) => {
          const stack = [...(data?.microlessonByHierarchy.sections ?? [])];
          while (stack.length) {
            const current = stack.pop();
            if (current) {
              if (current.uniqueId === nextUpUUID) {
                return current.oid;
              }
              if (current.sections) {
                stack.push(...current.sections);
              }
              if (current.content) {
                stack.push(...current.content);
              }
            }
          }
          return null;
        })
      )
      .toPromise();
  }

  getProductEnumFromName(name: string): ContentSubject[] {
    switch (name) {
      case 'Reading':
        return [ContentSubject.reading];
      case 'Math':
        return [ContentSubject.math];
      case 'Lectura':
        return [ContentSubject.lectura];
      case 'Matematicas':
        return [ContentSubject.matematicas];
      default:
        return [ContentSubject.all];
    }
  }
}
